<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      tableColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'date', label: 'Date' },
        { key: 'punch', label: 'Punch In / Out' },
        { key: 'hours', label: 'Hours' },
        { key: 'status', label: 'Status' },
        { key: 'source', label: 'Source' },
        { key: 'remarks', label: 'Remarks' },
      ],
      records: [],
      employees: [],
      filters: {
        employee_id: '',
        month: '',
        year: new Date().getFullYear(),
        status: '',
      },
      statusOptions: ['present', 'absent', 'half_day', 'late', 'on_leave', 'holiday'],
      months: [
        { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
        { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
        { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
        { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
      ],
      pagination: { current_page: 1, per_page: 50, last_page: 1, total: 0, links: [] },
    };
  },
  computed: {
    years() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2, y - 3];
    },
    hasFilters() {
      return this.filters.employee_id || this.filters.month || this.filters.status
        || this.filters.year !== new Date().getFullYear();
    },
  },
  mounted() {
    this.fetchEmployees();
    this.fetchRecords();
  },
  methods: {
    async fetchEmployees() {
      try {
        const res = await axios.get('/hrms/employees/dropdown');
        this.employees = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    async fetchRecords(page = 1) {
      this.loading = true;
      try {
        const res = await axios.get('/hrms/attendances', {
          params: {
            page,
            employee_id: this.filters.employee_id || undefined,
            month: this.filters.month || undefined,
            year: this.filters.year || undefined,
            status: this.filters.status || undefined,
          },
        });
        this.records = res.data.data.data;
        this.pagination = {
          current_page: res.data.data.current_page,
          per_page: res.data.data.per_page,
          last_page: res.data.data.last_page,
          total: res.data.data.total,
          links: res.data.data.links,
        };
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    applyFilters() { this.fetchRecords(1); },
    clearFilters() {
      this.filters = { employee_id: '', month: '', year: new Date().getFullYear(), status: '' };
      this.fetchRecords(1);
    },

    employeeLabel(e) {
      return e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim();
    },

    initials(name) {
      if (!name) return '?';
      const p = name.trim().split(/\s+/);
      return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
    },
    avatarColor(name) {
      const palette = [
        ['#eef2ff', '#4f46e5'], ['#ecfeff', '#0891b2'], ['#f0fdf4', '#16a34a'],
        ['#fff7ed', '#ea580c'], ['#fdf4ff', '#a21caf'], ['#fef2f2', '#dc2626'],
        ['#f5f3ff', '#7c3aed'], ['#fffbeb', '#b45309'],
      ];
      let h = 0;
      for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
      return palette[Math.abs(h) % palette.length];
    },

    fmtTime(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    statusLabel(s) {
      const map = { present: 'Present', absent: 'Absent', half_day: 'Half Day', late: 'Late', on_leave: 'On Leave', holiday: 'Holiday' };
      return map[s] || s || '—';
    },
    statusClass(s) {
      const map = {
        present: 'att-st att-present', absent: 'att-st att-absent', half_day: 'att-st att-half',
        late: 'att-st att-late', on_leave: 'att-st att-leave', holiday: 'att-st att-holiday',
      };
      return map[s] || 'att-st att-absent';
    },
    sourceLabel(s) {
      const map = { app: 'App', manual: 'Manual', upload: 'Upload' };
      return map[s] || s || '—';
    },
    sourceClass(s) {
      const map = { app: 'att-src att-app', manual: 'att-src att-manual', upload: 'att-src att-upload' };
      return map[s] || 'att-src att-manual';
    },

    openManualEntry() {
      this.$refs.manualModal.open();
      this.$openModal('hrm_attendance_manual_modal');
    },
    goRegularizations() { this.$router.push('/hrms/attendance-regularizations'); },
    goImport() { this.$router.push('/hrms/attendance-import'); },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center gap-3">
          <div class="att-head-ico"><i class="ti ti-calendar-time"></i></div>
          <div>
            <h4 class="mb-0 fw-bold" style="color:#0f172a;">Attendance</h4>
            <p class="mb-0" style="font-size:12.5px; color:#94a3b8;">Track and manage employee attendance records</p>
          </div>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2 mt-3 mt-md-0">
          <button class="att-ghost-btn" @click="goRegularizations">
            <i class="ti ti-clock-edit"></i>Regularizations
          </button>
          <button class="att-ghost-btn" @click="goImport">
            <i class="ti ti-upload"></i>Import CSV
          </button>
          <button class="att-primary-btn" @click="openManualEntry">
            <i class="ti ti-circle-plus"></i>Manual Entry
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="att-filter-bar mb-3">
        <div class="att-filter-field">
          <label>Employee</label>
          <select class="form-select form-select-sm" v-model="filters.employee_id">
            <option value="">All Employees</option>
            <option v-for="e in employees" :key="e.id" :value="e.id">{{ employeeLabel(e) }}</option>
          </select>
        </div>
        <div class="att-filter-field">
          <label>Month</label>
          <select class="form-select form-select-sm" v-model="filters.month">
            <option value="">All</option>
            <option v-for="m in months" :key="m.v" :value="m.v">{{ m.l }}</option>
          </select>
        </div>
        <div class="att-filter-field">
          <label>Year</label>
          <select class="form-select form-select-sm" v-model="filters.year">
            <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div class="att-filter-field">
          <label>Status</label>
          <select class="form-select form-select-sm" v-model="filters.status">
            <option value="">All Status</option>
            <option v-for="s in statusOptions" :key="s" :value="s">{{ statusLabel(s) }}</option>
          </select>
        </div>
        <div class="att-filter-actions">
          <button class="att-apply-btn" @click="applyFilters"><i class="ti ti-filter"></i>Apply</button>
          <button v-if="hasFilters" class="att-clear-btn" @click="clearFilters"><i class="ti ti-x"></i>Clear</button>
        </div>
      </div>

      <!-- Table card -->
      <div class="att-table-card">
        <div class="att-table-head">
          <h5>Attendance Records</h5>
          <span class="att-count">{{ pagination.total }} record{{ pagination.total === 1 ? '' : 's' }}</span>
        </div>
        <div class="att-table-body">
          <app-table
            :columns="tableColumns"
            :rows="records"
            :pagination="pagination"
            :loading="loading"
            empty-text="No attendance records found."
            empty-icon="ti ti-calendar-off"
            @page-change="fetchRecords"
          >
            <template #employee="{ row }">
              <div class="d-flex align-items-center gap-2">
                <span class="att-avatar"
                  :style="{ background: avatarColor(row.employee?.full_name)[0], color: avatarColor(row.employee?.full_name)[1] }">
                  {{ initials(row.employee?.full_name) }}
                </span>
                <div class="d-flex flex-column">
                  <span class="att-emp-name">{{ row.employee?.full_name || '—' }}</span>
                  <span v-if="row.employee?.code" class="att-code">{{ row.employee.code }}</span>
                </div>
              </div>
            </template>

            <template #date="{ row }">
              <span class="att-date">{{ $formatDate(row.date) }}</span>
            </template>

            <template #punch="{ row }">
              <div class="att-punch">
                <span class="att-in"><i class="ti ti-arrow-down-right"></i>{{ fmtTime(row.punch_in) }}</span>
                <span class="att-out"><i class="ti ti-arrow-up-right"></i>{{ fmtTime(row.punch_out) }}</span>
              </div>
            </template>

            <template #hours="{ row }">
              <span class="att-hours">{{ row.working_hours ? row.working_hours + 'h' : '—' }}</span>
            </template>

            <template #status="{ row }">
              <span :class="statusClass(row.status)"><span class="att-st-dot"></span>{{ statusLabel(row.status) }}</span>
            </template>

            <template #source="{ row }">
              <span :class="sourceClass(row.source)">{{ sourceLabel(row.source) }}</span>
            </template>

            <template #remarks="{ row }">
              <span class="att-remarks">{{ row.remarks || '—' }}</span>
            </template>
          </app-table>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-attendance-manual-modal ref="manualModal" @updated="fetchRecords" />
</template>

<style scoped>
/* header */
.att-head-ico {
  width: 46px; height: 46px; border-radius: 13px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px; box-shadow: 0 8px 18px -6px rgba(79,70,229,.5);
}

/* buttons */
.att-ghost-btn, .att-primary-btn, .att-apply-btn, .att-clear-btn {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 600; border-radius: 10px;
  padding: 9px 16px; cursor: pointer; border: 1px solid transparent;
  transition: all .15s ease;
}
.att-ghost-btn { background: #fff; color: #475569; border-color: #e2e8f0; }
.att-ghost-btn:hover { border-color: #cbd5e1; color: #1e293b; box-shadow: 0 2px 8px rgba(0,0,0,.05); }
.att-primary-btn { background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff; box-shadow: 0 8px 18px -8px rgba(79,70,229,.6); }
.att-primary-btn:hover { transform: translateY(-1px); box-shadow: 0 12px 22px -8px rgba(79,70,229,.7); }

/* filter bar */
.att-filter-bar {
  display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px;
  background: #fff; border: 1px solid #eef2f7; border-radius: 16px;
  padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,.03);
}
.att-filter-field { display: flex; flex-direction: column; min-width: 150px; }
.att-filter-field label { font-size: 10.5px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }
.att-filter-field .form-select { border-radius: 9px; border-color: #e2e8f0; font-size: 13px; }
.att-filter-actions { display: flex; gap: 8px; margin-left: auto; }
.att-apply-btn { background: #0f172a; color: #fff; }
.att-apply-btn:hover { background: #1e293b; }
.att-clear-btn { background: #f8fafc; color: #64748b; border-color: #e2e8f0; }
.att-clear-btn:hover { background: #f1f5f9; color: #475569; }

/* table card */
.att-table-card { background: #fff; border: 1px solid #eef2f7; border-radius: 16px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,.03); }
.att-table-head {
  display: flex; align-items: center; gap: 10px;
  padding: 18px 22px; border-bottom: 1px solid #f1f5f9;
}
.att-table-head h5 { margin: 0; font-size: 14.5px; font-weight: 700; color: #0f172a; }
.att-count {
  font-size: 11px; font-weight: 600; color: #64748b;
  background: #f1f5f9; padding: 3px 10px; border-radius: 20px;
}
.att-table-body { padding: 6px 10px 12px; }

/* cells */
.att-avatar {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12.5px; font-weight: 700; letter-spacing: .3px;
}
.att-emp-name { font-size: 12.5px; font-weight: 600; color: #1e293b; }
.att-code { font-size: 10px; font-weight: 700; letter-spacing: .4px; color: #94a3b8; }
.att-date { font-size: 12.5px; color: #475569; font-weight: 500; }

.att-punch { display: flex; flex-direction: column; gap: 2px; }
.att-in, .att-out { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; }
.att-in { color: #16a34a; }
.att-out { color: #dc2626; }
.att-in i, .att-out i { font-size: 12px; }

.att-hours { font-size: 12.5px; font-weight: 700; color: #0f172a; }
.att-remarks { font-size: 11px; color: #94a3b8; }

.att-st {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 10.5px; font-weight: 600; padding: 4px 11px; border-radius: 20px;
}
.att-st-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.att-present { background: #dcfce7; color: #16a34a; }
.att-absent { background: #fee2e2; color: #dc2626; }
.att-half { background: #fef3c7; color: #b45309; }
.att-late { background: #ffedd5; color: #ea580c; }
.att-leave { background: #e0e7ff; color: #4f46e5; }
.att-holiday { background: #f1f5f9; color: #64748b; }

.att-src { display: inline-block; font-size: 10px; font-weight: 600; padding: 3px 9px; border-radius: 6px; }
.att-app { background: #ecfeff; color: #0891b2; }
.att-manual { background: #f5f3ff; color: #7c3aed; }
.att-upload { background: #fff7ed; color: #c2410c; }

@media (max-width: 768px) {
  .att-filter-field { min-width: 100%; }
  .att-filter-actions { margin-left: 0; width: 100%; }
  .att-apply-btn, .att-clear-btn { flex: 1; justify-content: center; }
}
@media (max-width: 767px) {
  .att-head-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    width: 100%;
  }
  .att-head-actions .att-ghost-btn,
  .att-head-actions .att-primary-btn {
    width: 100%;
    justify-content: center;
    font-size: 12.5px;
    padding: 10px 8px;
  }
 
  .att-head-actions .att-primary-btn {
    grid-column: 1 / -1;
  }
}
</style>